/**
 * Game Socket Service
 *
 * This file provides a centralized Socket.IO service for game communication.
 * Handles all socket connections, event emissions, and event subscriptions.
 *
 * Used by:
 * - useGameSocket hook (primary consumer)
 * - GamePage (for initialization)
 *
 * Responsibilities:
 * - Socket connection management
 * - Event emission (emit game actions)
 * - Event subscription (listen for game updates)
 * - Reconnection handling
 * - Error handling
 *
 * This centralizes all socket logic that was previously scattered in RoomPage
 */

import { Socket } from 'socket.io-client';
import { createSocket } from '../../config/socket';
import { getAuthToken } from '../../utils/authToken';
import type {
    // Client emit types
    JoinRoomPayload,
    LeaveRoomPayload,
    PlayerReadyPayload,
    PlaceIslandsPayload,
    PlaceMonsterPayload,
    MoveMonsterPayload,
    UpdateMonsterDeductionPayload,
    UpdateMonsterDeductionResponse,
    GetPlayerDeductionsResponse,
    ChatMessagePayload,
    GetChatHistoryPayload,
    GetChatHistoryResponse,
    GetHistoryPayload,
    ChangeThemePayload,
    ChangeColorPayload,
    // Server listen types
    RoomUpdatePayload,
    PlayerJoinedPayload,
    PlayerLeftPayload,
    PlayerReadyUpdatePayload,
    PhaseChangedPayload,
    IslandsPlacedPayload,
    MonsterPlacedPayload,
    MonsterMovedPayload,
    GameEndedPayload,
    TurnChangedPayload,
    ChatMessageReceivedPayload,
    HistoryUpdatePayload,
    DisconnectTimerPayload,
    PlayerReconnectedPayload,
    PlayerDisconnectedPayload,
    SocketErrorPayload,
    // Response types
    SocketResponse,
    JoinRoomResponse,
    PlaceIslandsResponse,
    PlaceMonsterResponse,
    MoveMonsterResponse,
    ConcedeResponse,
    GetHistoryResponse,
    StartGameResponse,
} from '../types/socket.types';

export type ConnectionStatus = 'disconnected' | 'connecting' | 'connected' | 'error';

/**
 * GameSocketService - Centralized socket management
 */
interface QueuedSubscription {
    event: string;
    handler: (...args: any[]) => void;
    unsubscribe: () => void;
}

class GameSocketService {
    private socket: Socket | null = null;
    private connectionStatus: ConnectionStatus = 'disconnected';
    private statusListeners: Set<(status: ConnectionStatus) => void> = new Set();
    private currentRoom: JoinRoomPayload | null = null; // Store room info for automatic reconnection
    /** Fired after automatic re-join succeeds post-reconnect (includes `playerDeductions` when applicable). */
    private reconnectListeners: Set<(response: JoinRoomResponse) => void> = new Set();
    private subscriptionQueue: QueuedSubscription[] = []; // Queue for subscriptions made before socket is ready

    /**
     * Initialize socket connection.
     * Only creates a socket when none exists so we never replace the socket and lose
     * context handlers (onPhaseChanged, onIslandsPlaced, etc.). If the socket exists
     * but is disconnected, callers (e.g. connectAndJoinRoom) wait for it to connect.
     */
    connect(): void {
        if (this.socket) {
            if (this.socket.connected) {
                console.warn('Socket already connected');
            }
            return;
        }

        this.updateStatus('connecting');

        // One token for both user and guest (from authToken); always send if present
        const token = getAuthToken();
        this.socket = createSocket(token ? { token } : {});

        // Apply all queued subscriptions now that socket is created
        // (Socket.IO allows listeners to be set up before connection)
        this._applyQueuedSubscriptions();

        // Connection event handlers
        this.socket.on('connect', () => {
            console.log('✅ Game socket connected:', this.socket?.id);
            this.updateStatus('connected');
        });

        this.socket.on('disconnect', (reason) => {
            console.log('❌ Game socket disconnected:', reason);
            this.updateStatus('disconnected');
        });

        this.socket.on('connect_error', (error) => {
            console.error('🔴 Game socket connection error:', error.message);
            this.updateStatus('error');
        });

        this.socket.on('reconnect', (attemptNumber) => {
            console.log('🔄 Game socket reconnected after', attemptNumber, 'attempts');
            this.updateStatus('connected');

            // Automatically re-join room if we were in one
            if (this.currentRoom) {
                console.log('🔄 Re-joining room after reconnection...');
                this._joinRoomInternal(this.currentRoom, (response) => {
                    if (response.success) {
                        console.log('✅ Successfully re-joined room:', this.currentRoom?.roomName);
                        this.reconnectListeners.forEach(listener => listener(response));
                    } else {
                        console.error('❌ Failed to re-join room:', response.error);
                    }
                });
            }
        });

        this.socket.on('reconnect_error', (error) => {
            console.error('🔴 Game socket reconnection error:', error.message);
            this.updateStatus('error');
        });

        this.socket.on('reconnect_failed', () => {
            console.error('🔴 Game socket reconnection failed');
            this.updateStatus('error');
        });
    }

    /**
     * Disconnect socket.
     * Clears all listeners before disconnecting so the socket is torn down cleanly.
     */
    disconnect(): void {
        if (this.socket) {
            this.socket.removeAllListeners();
            this.socket.disconnect();
            this.socket = null;
            this.currentRoom = null; // Clear room info on disconnect
            this.updateStatus('disconnected');
        }
    }

    /**
     * Get current socket instance
     */
    getSocket(): Socket | null {
        return this.socket;
    }

    /**
     * Get connection status
     */
    getStatus(): ConnectionStatus {
        return this.connectionStatus;
    }

    /**
     * Subscribe to connection status changes
     */
    onStatusChange(listener: (status: ConnectionStatus) => void): () => void {
        this.statusListeners.add(listener);
        // Immediately call with current status
        listener(this.connectionStatus);
        // Return unsubscribe function
        return () => {
            this.statusListeners.delete(listener);
        };
    }

    /**
     * Update connection status and notify listeners
     */
    private updateStatus(status: ConnectionStatus): void {
        this.connectionStatus = status;
        this.statusListeners.forEach(listener => listener(status));
    }

    // ============================================================================
    // Event Emission Methods (Client -> Server)
    // ============================================================================

    /**
     * Internal method to join room (assumes socket is connected)
     */
    private _joinRoomInternal(
        payload: JoinRoomPayload,
        callback?: (response: JoinRoomResponse) => void
    ): void {
        if (!this.socket?.connected) {
            console.error('Cannot join room: socket not connected');
            callback?.({
                success: false,
                error: 'Socket not connected',
            });
            return;
        }

        this.socket.emit('join room', payload, callback);
    }

    /**
     * Join a room (requires socket to be connected)
     * For automatic connection + join, use connectAndJoinRoom instead
     */
    joinRoom(
        payload: JoinRoomPayload,
        callback?: (response: JoinRoomResponse) => void
    ): void {
        this._joinRoomInternal(payload, callback);
    }

    /**
     * Connect to socket and join room in one atomic operation.
     * Automatically handles reconnection and re-joining.
     * 
     * @param payload - Room join information
     * @param options - Optional configuration (timeout, retries)
     * @returns Promise that resolves when room is joined
     */
    async connectAndJoinRoom(
        payload: JoinRoomPayload,
        options: {
            timeout?: number; // Timeout in milliseconds (default: 30000)
            maxRetries?: number; // Max retry attempts (default: 3)
        } = {}
    ): Promise<JoinRoomResponse> {
        const { timeout = 30000, maxRetries = 3 } = options;

        // Store room info for automatic reconnection
        this.currentRoom = payload;

        // If already connected, just join room
        if (this.socket?.connected) {
            return new Promise((resolve, reject) => {
                this._joinRoomInternal(payload, (response) => {
                    if (response.success) {
                        resolve(response);
                    } else {
                        reject(new Error(response.error || 'Failed to join room'));
                    }
                });
            });
        }

        // Connect first, then join
        return new Promise((resolve, reject) => {
            let resolved = false;
            let timeoutId: NodeJS.Timeout | null = null;

            const cleanup = () => {
                if (timeoutId) {
                    clearTimeout(timeoutId);
                    timeoutId = null;
                }
                if (this.socket) {
                    this.socket.off('connect', onConnect);
                    this.socket.off('connect_error', onConnectError);
                }
            };

            const onConnect = () => {
                if (resolved) return;
                cleanup();

                // Now join the room
                this._joinRoomInternal(payload, (response) => {
                    if (resolved) return;
                    resolved = true;

                    if (response.success) {
                        resolve(response);
                    } else {
                        // Retry logic for room join failures
                        this._retryJoinRoom(payload, maxRetries)
                            .then(resolve)
                            .catch(reject);
                    }
                });
            };

            const onConnectError = (error: Error) => {
                if (resolved) return;
                cleanup();
                resolved = true;
                reject(new Error(`Connection failed: ${error.message}`));
            };

            // Set up timeout
            timeoutId = setTimeout(() => {
                if (resolved) return;
                cleanup();
                resolved = true;
                reject(new Error('Connection and room join timeout'));
            }, timeout);

            // If socket exists but not connected, wait for it to connect
            // Otherwise, create a new connection
            if (this.socket && !this.socket.connected) {
                // Socket exists but not connected yet, wait for it
                this.socket.on('connect', onConnect);
                this.socket.on('connect_error', onConnectError);
            } else {
                // No socket exists, create one
                this.connect();

                // Set up event listeners
                if (this.socket) {
                    this.socket.on('connect', onConnect);
                    this.socket.on('connect_error', onConnectError);
                } else {
                    cleanup();
                    resolved = true;
                    reject(new Error('Failed to initialize socket'));
                }
            }
        });
    }

    /**
     * Retry room join with exponential backoff
     */
    private async _retryJoinRoom(
        payload: JoinRoomPayload,
        maxRetries: number
    ): Promise<JoinRoomResponse> {
        let lastError: Error | null = null;

        for (let attempt = 0; attempt < maxRetries; attempt++) {
            try {
                return await new Promise<JoinRoomResponse>((resolve, reject) => {
                    this._joinRoomInternal(payload, (response) => {
                        if (response.success) {
                            resolve(response);
                        } else {
                            reject(new Error(response.error || 'Failed to join room'));
                        }
                    });
                });
            } catch (error) {
                lastError = error as Error;
                if (attempt < maxRetries - 1) {
                    // Exponential backoff: 1s, 2s, 4s
                    const delay = 1000 * Math.pow(2, attempt);
                    console.log(`Retrying room join in ${delay}ms (attempt ${attempt + 1}/${maxRetries})...`);
                    await new Promise(resolve => setTimeout(resolve, delay));
                }
            }
        }

        throw lastError || new Error('Failed to join room after retries');
    }

    /**
     * Subscribe to reconnection events (when socket reconnects and re-joins room)
     */
    onReconnected(listener: (response: JoinRoomResponse) => void): () => void {
        this.reconnectListeners.add(listener);
        return () => {
            this.reconnectListeners.delete(listener);
        };
    }

    /**
     * Get current room info (for debugging/status)
     */
    getCurrentRoom(): JoinRoomPayload | null {
        return this.currentRoom;
    }

    /**
     * Leave a room
     */
    leaveRoom(
        payload: LeaveRoomPayload,
        callback?: (response: SocketResponse) => void
    ): void {
        // Clear current room if leaving the room we're in
        if (this.currentRoom && this.currentRoom.roomName === payload.roomName) {
            this.currentRoom = null;
        }

        if (!this.socket?.connected) {
            console.error('Cannot leave room: socket not connected');
            callback?.({
                success: false,
                error: 'Socket not connected',
            });
            return;
        }

        this.socket.emit('leave room', payload, callback);
    }

    /**
     * Toggle player ready state
     */
    playerReady(
        payload: PlayerReadyPayload,
        callback?: (response: SocketResponse) => void
    ): void {
        if (!this.socket?.connected) {
            console.error('Cannot set ready: socket not connected');
            callback?.({
                success: false,
                error: 'Socket not connected',
            });
            return;
        }

        this.socket.emit('player ready', payload, callback);
    }

    /**
     * Place islands
     */
    placeIslands(
        payload: PlaceIslandsPayload,
        callback?: (response: PlaceIslandsResponse) => void
    ): void {
        if (!this.socket?.connected) {
            console.error('Cannot place islands: socket not connected');
            callback?.({
                success: false,
                error: 'Socket not connected',
            });
            return;
        }

        this.socket.emit('place islands', payload, callback);
    }

    /**
     * Place a monster
     */
    placeMonster(
        payload: PlaceMonsterPayload,
        callback?: (response: PlaceMonsterResponse) => void
    ): void {
        if (!this.socket?.connected) {
            console.error('Cannot place monster: socket not connected');
            callback?.({
                success: false,
                error: 'Socket not connected',
            });
            return;
        }

        this.socket.emit('place monster', payload, callback);
    }

    /**
     * Move a monster
     */
    moveMonster(
        payload: MoveMonsterPayload,
        callback?: (response: MoveMonsterResponse) => void
    ): void {
        if (!this.socket?.connected) {
            console.error('Cannot move monster: socket not connected');
            callback?.({
                success: false,
                error: 'Socket not connected',
            });
            return;
        }

        this.socket.emit('move monster', payload, callback);
    }

    /**
     * Forfeit the match — opponent wins (only valid during ACTIVE phase; server broadcasts `game ended`).
     */
    concede(callback?: (response: ConcedeResponse) => void): void {
        if (!this.socket?.connected) {
            console.error('Cannot concede: socket not connected');
            callback?.({
                success: false,
                error: 'Socket not connected',
            });
            return;
        }

        this.socket.emit('concede', {}, callback);
    }

    /**
     * Persist deduction (xed / guess) for one opponent monster to Redis (viewer-scoped).
     */
    updateMonsterDeduction(
        payload: UpdateMonsterDeductionPayload,
        callback?: (response: UpdateMonsterDeductionResponse) => void
    ): void {
        if (!this.socket?.connected) {
            console.error('Cannot update deduction: socket not connected');
            callback?.({
                success: false,
                error: 'Socket not connected',
            });
            return;
        }

        this.socket.emit('update monster deduction', payload, callback);
    }

    /**
     * Load viewer's deduction document from Redis (e.g. lobby → in-game without re-join).
     */
    getPlayerDeductions(callback?: (response: GetPlayerDeductionsResponse) => void): void {
        if (!this.socket?.connected) {
            console.error('Cannot get deductions: socket not connected');
            callback?.({
                success: false,
                error: 'Socket not connected',
            });
            return;
        }

        this.socket.emit('get player deductions', {}, callback);
    }

    /**
     * Send chat message
     */
    sendChatMessage(
        payload: ChatMessagePayload,
        callback?: (response: SocketResponse) => void
    ): void {
        if (!this.socket?.connected) {
            console.error('Cannot send chat: socket not connected');
            callback?.({
                success: false,
                error: 'Socket not connected',
            });
            return;
        }

        this.socket.emit('chat message', payload, callback);
    }

    /**
     * Get chat history for current room
     */
    getChatHistory(
        payload: GetChatHistoryPayload,
        callback?: (response: GetChatHistoryResponse) => void
    ): void {
        if (!this.socket?.connected) {
            console.error('Cannot get chat history: socket not connected');
            callback?.({ success: false, error: 'Socket not connected' });
            return;
        }

        this.socket.emit('get chat history', payload ?? {}, callback);
    }

    /**
     * Get move history
     */
    getHistory(
        payload: GetHistoryPayload,
        callback?: (response: GetHistoryResponse) => void
    ): void {
        if (!this.socket?.connected) {
            console.error('Cannot get history: socket not connected');
            callback?.({
                success: false,
                error: 'Socket not connected',
                data: null,
            });
            return;
        }

        this.socket.emit('get history', payload, callback);
    }

    /**
     * Change theme
     */
    changeTheme(
        payload: ChangeThemePayload,
        callback?: (response: SocketResponse) => void
    ): void {
        if (!this.socket?.connected) {
            console.error('Cannot change theme: socket not connected');
            callback?.({
                success: false,
                error: 'Socket not connected',
            });
            return;
        }

        this.socket.emit('change theme', payload, callback);
    }

    /**
     * Change player color
     */
    changeColor(
        payload: ChangeColorPayload,
        callback?: (response: SocketResponse) => void
    ): void {
        if (!this.socket?.connected) {
            console.error('Cannot change color: socket not connected');
            callback?.({
                success: false,
                error: 'Socket not connected',
            });
            return;
        }

        this.socket.emit('change color', payload, callback);
    }

    /**
     * Start the game (both players must be ready in 2P; only human ready in bot game)
     */
    startGame(callback?: (response: StartGameResponse) => void): void {
        if (!this.socket?.connected) {
            console.error('Cannot start game: socket not connected');
            callback?.({
                success: false,
                error: 'Socket not connected',
            });
            return;
        }

        this.socket.emit('start game', {}, callback);
    }

    // ============================================================================
    // Event Subscription Methods (Server -> Client)
    // ============================================================================

    /**
     * Apply all queued subscriptions to the socket
     */
    private _applyQueuedSubscriptions(): void {
        if (!this.socket) return;

        const subscriptions = [...this.subscriptionQueue];
        this.subscriptionQueue = [];

        subscriptions.forEach(({ event, handler }) => {
            this.socket?.on(event, handler);
        });

        if (subscriptions.length > 0) {
            console.log(`✅ Applied ${subscriptions.length} queued subscriptions`);
        }
    }

    /**
     * Queue a subscription to be applied when socket is ready
     */
    private _queueSubscription(event: string, handler: (...args: any[]) => void): () => void {
        const unsubscribe = () => {
            // Remove from queue if not yet applied
            const index = this.subscriptionQueue.findIndex(
                sub => sub.event === event && sub.handler === handler
            );
            if (index !== -1) {
                this.subscriptionQueue.splice(index, 1);
            } else if (this.socket) {
                // Already applied, unsubscribe from socket
                this.socket.off(event, handler);
            }
        };

        this.subscriptionQueue.push({ event, handler, unsubscribe });
        return unsubscribe;
    }

    /**
     * Subscribe to room update events
     */
    onRoomUpdate(handler: (payload: RoomUpdatePayload) => void): () => void {
        if (!this.socket) {
            // Queue subscription for when socket is ready
            return this._queueSubscription('room update', handler);
        }

        this.socket.on('room update', handler);
        return () => {
            this.socket?.off('room update', handler);
        };
    }

    /**
     * Subscribe to player joined events
     */
    onPlayerJoined(handler: (payload: PlayerJoinedPayload) => void): () => void {
        if (!this.socket) {
            return this._queueSubscription('player joined', handler);
        }

        this.socket.on('player joined', handler);
        return () => {
            this.socket?.off('player joined', handler);
        };
    }

    /**
     * Subscribe to player left events
     */
    onPlayerLeft(handler: (payload: PlayerLeftPayload) => void): () => void {
        if (!this.socket) {
            return this._queueSubscription('player left', handler);
        }

        this.socket.on('player left', handler);
        return () => {
            this.socket?.off('player left', handler);
        };
    }

    /**
     * Subscribe to player ready update events
     */
    onPlayerReadyUpdate(handler: (payload: PlayerReadyUpdatePayload) => void): () => void {
        if (!this.socket) {
            return this._queueSubscription('player ready update', handler);
        }

        this.socket.on('player ready update', handler);
        return () => {
            this.socket?.off('player ready update', handler);
        };
    }

    /**
     * Subscribe to phase changed events
     */
    onPhaseChanged(handler: (payload: PhaseChangedPayload) => void): () => void {
        if (!this.socket) {
            return this._queueSubscription('phase changed', handler);
        }

        this.socket.on('phase changed', handler);
        return () => {
            this.socket?.off('phase changed', handler);
        };
    }

    /**
     * Subscribe to islands placed events
     */
    onIslandsPlaced(handler: (payload: IslandsPlacedPayload) => void): () => void {
        if (!this.socket) {
            return this._queueSubscription('islands placed', handler);
        }

        this.socket.on('islands placed', handler);
        return () => {
            this.socket?.off('islands placed', handler);
        };
    }

    /**
     * Subscribe to monster placed events
     */
    onMonsterPlaced(handler: (payload: MonsterPlacedPayload) => void): () => void {
        if (!this.socket) {
            return this._queueSubscription('monster placed', handler);
        }

        this.socket.on('monster placed', handler);
        return () => {
            this.socket?.off('monster placed', handler);
        };
    }

    /**
     * Subscribe to monster moved events
     */
    onMonsterMoved(handler: (payload: MonsterMovedPayload) => void): () => void {
        if (!this.socket) {
            return this._queueSubscription('monster moved', handler);
        }

        this.socket.on('monster moved', handler);
        return () => {
            this.socket?.off('monster moved', handler);
        };
    }

    /**
     * Subscribe to game ended events
     */
    onGameEnded(handler: (payload: GameEndedPayload) => void): () => void {
        if (!this.socket) {
            return this._queueSubscription('game ended', handler);
        }

        this.socket.on('game ended', handler);
        return () => {
            this.socket?.off('game ended', handler);
        };
    }

    /**
     * Subscribe to turn changed events
     */
    onTurnChanged(handler: (payload: TurnChangedPayload) => void): () => void {
        if (!this.socket) {
            return this._queueSubscription('turn changed', handler);
        }

        this.socket.on('turn changed', handler);
        return () => {
            this.socket?.off('turn changed', handler);
        };
    }

    /**
     * Subscribe to chat message events
     */
    onChatMessage(handler: (payload: ChatMessageReceivedPayload) => void): () => void {
        if (!this.socket) {
            return this._queueSubscription('chat message', handler);
        }

        this.socket.on('chat message', handler);
        return () => {
            this.socket?.off('chat message', handler);
        };
    }

    /**
     * Subscribe to history update events
     */
    onHistoryUpdate(handler: (payload: HistoryUpdatePayload) => void): () => void {
        if (!this.socket) {
            return this._queueSubscription('history update', handler);
        }

        this.socket.on('history update', handler);
        return () => {
            this.socket?.off('history update', handler);
        };
    }

    /**
     * Subscribe to disconnect timer events
     */
    onDisconnectTimer(handler: (payload: DisconnectTimerPayload) => void): () => void {
        if (!this.socket) {
            return this._queueSubscription('disconnect timer', handler);
        }

        this.socket.on('disconnect timer', handler);
        return () => {
            this.socket?.off('disconnect timer', handler);
        };
    }

    /**
     * Subscribe to player reconnected events
     */
    onPlayerReconnected(handler: (payload: PlayerReconnectedPayload) => void): () => void {
        if (!this.socket) {
            return this._queueSubscription('player reconnected', handler);
        }

        this.socket.on('player reconnected', handler);
        return () => {
            this.socket?.off('player reconnected', handler);
        };
    }

    /**
     * Subscribe to player disconnected events
     */
    onPlayerDisconnected(handler: (payload: PlayerDisconnectedPayload) => void): () => void {
        if (!this.socket) {
            return this._queueSubscription('player disconnected', handler);
        }

        this.socket.on('player disconnected', handler);
        return () => {
            this.socket?.off('player disconnected', handler);
        };
    }

    /**
     * Subscribe to error events
     */
    onError(handler: (payload: SocketErrorPayload) => void): () => void {
        if (!this.socket) {
            return this._queueSubscription('error', handler);
        }

        this.socket.on('error', handler);
        return () => {
            this.socket?.off('error', handler);
        };
    }

    /**
     * Remove all event listeners
     */
    removeAllListeners(): void {
        if (this.socket) {
            this.socket.removeAllListeners();
        }
    }
}

// Export singleton instance
export const gameSocketService = new GameSocketService();
export default gameSocketService;
